import React from 'react';
import { Box, Typography, Button, Popover, TextField, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import { responsiveTypography } from '../../themes/responsive';

/**
 * Popover for extending experiments with new dates and status
 */
const ExtendExperimentPopover = ({
  open,
  anchorEl,
  onClose,
  onExtend,
  currentEndDate,
  loading = false,
  t
}) => {
  // Get current language for date formatting
  const { i18n } = useTranslation();
  
  // Initialize start date to day after current end date
  const getInitialStartDate = () => {
    if (currentEndDate) {
      const date = new Date(currentEndDate);
      date.setDate(date.getDate() + 1);
      return date;
    }
    return new Date();
  };

  const [startDate, setStartDate] = React.useState(getInitialStartDate());
  const [weeks, setWeeks] = React.useState(4);
  const [weeksInput, setWeeksInput] = React.useState('4');
  const [status, setStatus] = React.useState('pending');

  // Reset when opening with new end date
  React.useEffect(() => {
    if (open) {
      const newStartDate = getInitialStartDate();
      setStartDate(newStartDate);
      setWeeks(4);
      setWeeksInput('4');
      setStatus('pending');
    }
  }, [open, currentEndDate]);

  // Calculate end date based on start date and weeks
  const calculateEndDate = (start, weekCount) => {
    const end = new Date(start);
    end.setDate(end.getDate() + (weekCount * 7));
    return end;
  };

  const endDate = calculateEndDate(startDate, weeks);

  const handleSave = () => {
    // Format dates in local time
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    onExtend({
      dateFrom: formatLocalDate(startDate),
      dateTo: formatLocalDate(endDate),
      status: status
    });
  };

  const statusOptions = [
    { value: 'pending', label: t('pending') },
    { value: 'accepted', label: t('accepted') },
    { value: 'running', label: t('running') },
    { value: 'finished', label: t('finished') },
    { value: 'rejected', label: t('rejected') }
  ];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          border: `1px solid ${colors.border.light}`,
          boxShadow: `0 4px 20px ${colors.shadow.soft}`,
          p: '12px',
          minWidth: '280px'
        }
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Header */}
          <Typography sx={{
            ...responsiveTypography.small,
            fontWeight: 600,
            color: colors.text.primary
          }}>
            {t('extendExperiment')}
          </Typography>

          {/* Start Date Picker */}
          <Box>
            <Typography sx={{
              ...responsiveTypography.small,
              color: colors.text.secondary,
              mb: '4px'
            }}>
              {t('startDate')}
            </Typography>
            <DatePicker
              value={startDate}
              onChange={(newDate) => setStartDate(newDate || new Date())}
              minDate={getInitialStartDate()}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      fontSize: '12px',
                      height: '32px'
                    }
                  }
                }
              }}
            />
          </Box>

          {/* Duration in Weeks */}
          <Box>
            <Typography sx={{
              ...responsiveTypography.small,
              color: colors.text.secondary,
              mb: '4px'
            }}>
              {t('durationWeeks')}
            </Typography>
            <TextField
              type="number"
              value={weeksInput}
              onChange={(e) => {
                const value = e.target.value;
                setWeeksInput(value);
                
                const numValue = parseInt(value);
                if (!isNaN(numValue) && numValue >= 1 && numValue <= 52) {
                  setWeeks(numValue);
                } else if (value === '') {
                  setWeeks(1);
                }
              }}
              onBlur={(e) => {
                if (weeksInput === '' || parseInt(weeksInput) < 1) {
                  setWeeksInput('1');
                  setWeeks(1);
                }
              }}
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 52 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '12px',
                  height: '32px'
                }
              }}
            />
          </Box>

          {/* Status Dropdown */}
          <Box>
            <Typography sx={{
              ...responsiveTypography.small,
              color: colors.text.secondary,
              mb: '4px'
            }}>
              {t('status')}
            </Typography>
            <TextField
              select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '12px',
                  height: '32px'
                }
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Preview */}
          <Box sx={{
            backgroundColor: colors.background.subtle,
            border: `1px solid ${colors.border.light}`,
            borderRadius: '4px',
            p: '8px'
          }}>
            <Typography sx={{
              ...responsiveTypography.small,
              color: colors.text.secondary,
              mb: '2px'
            }}>
              {t('preview')}:
            </Typography>
            <Typography sx={{
              ...responsiveTypography.small,
              color: colors.text.primary,
              fontWeight: 600
            }}>
              {t('from')} {startDate.toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { day: 'numeric', month: 'long' })} {t('to')} {endDate.toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { day: 'numeric', month: 'long' })}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button
              onClick={onClose}
              size="small"
              sx={{
                color: colors.text.secondary,
                ...responsiveTypography.small,
                textTransform: 'none',
                minWidth: '60px'
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              size="small"
              variant="contained"
              sx={{
                backgroundColor: colors.action.primary,
                color: 'white',
                ...responsiveTypography.small,
                textTransform: 'none',
                minWidth: '60px',
                '&:hover': {
                  backgroundColor: colors.action.primaryDark || '#1976d2',
                  color: 'white'
                },
                '&:disabled': {
                  backgroundColor: colors.action.primary,
                  color: 'white',
                  opacity: 0.6
                }
              }}
            >
              {loading ? t('loading') : t('extend')}
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>
    </Popover>
  );
};

export default ExtendExperimentPopover;